import { FormLayout } from 'presentation/ui/layouts/form-layout/FormLayout';
import { FormLayoutModifier } from 'presentation/ui/layouts/form-layout/FormLayoutModifier';
import { useContext } from 'react';

import { L10nContext } from 'context/L10nContext';

import { ColumnLayoutSection } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSection';
import { ColumnLayoutSectionMode } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSectionMode';
import { ColumnLayout } from 'presentation/ui/layouts/column-layout/ColumnLayout';
import { FormLayoutHeader } from 'presentation/ui/layouts/form-layout/form-layout-header/FormLayoutHeader';
import { FormLayoutSection } from 'presentation/ui/layouts/form-layout/form-layout-section/FormLayoutSection';
import { FormLayoutSectionAlign } from 'presentation/ui/layouts/form-layout/FormLayoutSectionAlign';
import { ButtonPrimary } from 'presentation/ui/partials/button/button-primary/ButtonPrimary';
import { ButtonSecondary } from 'presentation/ui/partials/button/button-secondary/ButtonSecondary';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { InputText, InputTextStatus } from 'presentation/ui/partials/input/input-text/InputText';
import { Label } from 'presentation/ui/partials/input/label/Label';
import { WasteManagementReportViewModel } from '../../../../domain/models/WasteManagementReportModel';

export interface WasteManagementGeneratorReportUpdateFormStep2Props {
	report: WasteManagementReportViewModel;
	onClickBack: () => void;
	onClickNext: () => void;
}

export const WasteManagementGeneratorReportUpdateFormStep2 = (props: WasteManagementGeneratorReportUpdateFormStep2Props): JSX.Element => {
	const { report, onClickBack, onClickNext } = props;

	const l10nContext = useContext(L10nContext);

	const periodDateStartFormatted = l10nContext.formatDateTime(report?.PeriodDateStart);
	const periodDateEndFormatted = l10nContext.formatDateTime(report?.PeriodDateEnd);
	const validUntilFormatted = l10nContext.formatDateTime(report?.ValidUntil);

	return (
		<FormLayout align={FormLayoutSectionAlign.ALIGN_HORIZONTAL} onSubmit={onClickNext} modifier={FormLayoutModifier.BLUE_BACKGROUND}>
			<FormLayoutHeader
				text={l10nContext.translate('common.modal.checkDetails', 'Überprüfen Sie bitte Ihre Angaben')}
				icon={IconIdentifier.WARNING}
			/>

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('reports.name', 'Name')}*`}
					thin
				>
					<InputText
						type='text'
						value={report.Name ?? ''}
						status={InputTextStatus.SUMMARY}
						disabled
						required
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('reports.periodDateStart', 'Startdatum')}*`}
					thin
				>
					<InputText
						type='text'
						value={periodDateStartFormatted}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('reports.periodDateEnd', 'Enddatum')}*`}
					thin
				>
					<InputText
						type='text'
						value={periodDateEndFormatted}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('reports.validUntil', 'Gültig bis')}*`}
					thin
				>
					<InputText
						type='text'
						value={validUntilFormatted}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={l10nContext.translate('reports.password', 'Passwort')}
					thin
				>
					<InputText
						type='text'
						defaultValue={report.Password ?? ''}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection align={FormLayoutSectionAlign.ALIGN_BOTTOM}>
				<ColumnLayout>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FLEX}>
						<ButtonSecondary
							buttonText={l10nContext.translate('common.button.back', 'Zurück')}
							onClick={onClickBack}
						/>
					</ColumnLayoutSection>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FIXED}>
						<ButtonPrimary
							buttonText={l10nContext.translate('common.button.saveChanges', 'Änderungen speichern')}
							type="submit"
						/>
					</ColumnLayoutSection>
				</ColumnLayout>
			</FormLayoutSection>
		</FormLayout>
	);
};
